import { useNavigate } from "@tanstack/react-router";
import {
  getControlName,
  getEndpointPartnerEventCodes,
  getPartnerEventName,
  getValidControlCategory,
  isDiscoveredDevicesControl,
  isEndpointManagementControl,
} from "@theprelude/core";
import { Button, ExternalLinkIcon } from "@theprelude/ds";
import {
  ControlCategories,
  ControlCodes,
  ITEM_ROOT,
  PartnerEndpoints,
  PartnerEvents,
  ValidControlCode,
  ValidPartnerEventCode,
} from "@theprelude/sdk";
import { ReactNode, useMemo } from "react";
import { z } from "zod";
import { PolicyHostsReducedFunctionalityDeepLink } from "@/components/deep-links";
import { DefaultOverlay } from "@/components/default-overlay";
import { Filter } from "@/lib/filter";
import { EndpointsTable } from "../endpoint-tables/endpoints-table";
import { buildViewSchema, getView, ViewDefinition } from "./schema";
import { EndpointsView } from "./views";

const overlay = "endpoints";
const defaultFilters: never[] = [];

export const endpointsSchema = buildViewSchema({
  overlay,
  views: EndpointsView,
  default: EndpointsView.ALL,
});

export type EndpointsSchema = NonNullable<z.infer<typeof endpointsSchema>[1]>;

const endpointCategoryEventCodes = new Set<ValidPartnerEventCode>([
  PartnerEvents.NO_ASSET_MANAGER,
  PartnerEvents.NO_EDR,
  PartnerEvents.NO_SERVER_MANAGER,
  PartnerEvents.NO_VULN_MANAGER,
]);

export function EndpointsOverlay({
  view = EndpointsView.ALL,
  filters = defaultFilters,
  control,
  instanceId,
}: EndpointsSchema) {
  const navigate = useNavigate();

  const onFilter = (filters: Filter[]) =>
    navigate({
      to: window.location.pathname,
      params: (prev) => prev,
      search: (prev) => ({
        ...prev,
        overlay: [overlay, { view, filters, control, instanceId }],
      }),
    });

  const { title, description, query, controlCategory, noControlEventCodes } =
    useMemo(() => getEndpointsView(view, control), [control, view]);

  return (
    <DefaultOverlay title={title}>
      <div className="flex h-full flex-col gap-4 p-4 pb-0">
        {description && <div>{description}</div>}
        <EndpointsTable
          query={query}
          filters={filters}
          controlCategory={controlCategory}
          noControlEventCodes={noControlEventCodes}
          control={control}
          instanceId={instanceId}
          onFilter={onFilter}
        />
      </div>
    </DefaultOverlay>
  );
}

const getEndpointsView = (
  view: EndpointsView,
  control: ValidControlCode | undefined,
) => {
  const result = getView(views, view, control);

  const controlCategory =
    result.controlCategory || (control && getValidControlCategory(control));

  // Exclude event filters like RFM in No EDR since such event will never be set in those scenarios.
  const noControlEventCodes = getEndpointPartnerEventCodes(
    controlCategory,
  ).filter((event) => control || endpointCategoryEventCodes.has(event));

  return { ...result, controlCategory, noControlEventCodes };
};

const views: Record<EndpointsView, ViewDefinition<PartnerEndpoints>> = {
  [EndpointsView.ALL]: {
    title: (control) =>
      control ? `${getControlName(control)} Endpoints` : "All Endpoints",
    description: (control) => {
      if (!control) {
        return undefined;
      }
      if (isDiscoveredDevicesControl(control)) {
        return "These endpoints were found in your discoverable devices.";
      }
      if (isEndpointManagementControl(control)) {
        return "These endpoints were found in your asset manager.";
      }
      return undefined;
    },
    query: {},
  },
  [EndpointsView.ALL_EDR]: {
    title: `Endpoints with EDR`,
    query: { missing_edr: false },
    controlCategory: ControlCategories.XDR,
  },
  [EndpointsView.ALL_ENDPOINT_MANAGEMENT]: {
    title: `Endpoints with Endpoint Management`,
    query: { missing_asset_manager: false, missing_server_manager: false },
    controlCategory: ControlCategories.ASSET_MANAGER,
  },
  [EndpointsView.ALL_HOST_FIREWALL]: {
    title: `Endpoints with Host Firewall`,
    query: {
      missing_host_firewall: false,
      platforms: {
        any: {
          or: [
            { [ITEM_ROOT]: { eq: "windows" } },
            { [ITEM_ROOT]: { eq: "darwin" } },
          ],
        },
      },
    },
    controlCategory: ControlCategories.HOST_FIREWALL,
  },
  [EndpointsView.ALL_VULNERABILITY_MANAGEMENT]: {
    title: `Endpoints with Vulnerability Management`,
    query: {
      or: [{ missing_vuln_manager: false }, { missing_vuln_scan: false }],
    },
    controlCategory: ControlCategories.VULN_MANAGER,
  },
  [EndpointsView.ALL_DISCOVERED_DEVICES]: {
    title: "Discovered Devices",
    query: {
      instances: {
        any: { category: { eq: ControlCategories.DISCOVERED_DEVICES } },
      },
    },
    controlCategory: ControlCategories.DISCOVERED_DEVICES,
  },
  [EndpointsView.NO_EDR]: {
    title: `Endpoints with ${getPartnerEventName(PartnerEvents.NO_EDR)}`,
    query: { missing_edr: true },
    partnerEvents: [PartnerEvents.NO_EDR],
    controlCategory: ControlCategories.XDR,
  },
  [EndpointsView.NO_ENDPOINT_MANAGER]: {
    title: `Endpoints with ${getPartnerEventName(PartnerEvents.NO_ASSET_MANAGER)}`,
    query: { missing_asset_manager: true },
    partnerEvents: [PartnerEvents.NO_ASSET_MANAGER],
    controlCategory: ControlCategories.ASSET_MANAGER,
  },
  [EndpointsView.NO_HOST_FIREWALL]: {
    title: `Endpoints with ${getPartnerEventName(PartnerEvents.NO_HOST_FIREWALL)}`,
    query: {
      missing_host_firewall: true,
    },
    partnerEvents: [PartnerEvents.NO_HOST_FIREWALL],
    controlCategory: ControlCategories.HOST_FIREWALL,
  },
  [EndpointsView.NO_SERVER_MANAGER]: {
    title: `Endpoints with ${getPartnerEventName(PartnerEvents.NO_SERVER_MANAGER)}`,
    query: { missing_server_manager: true },
    partnerEvents: [PartnerEvents.NO_SERVER_MANAGER],
    controlCategory: ControlCategories.ASSET_MANAGER,
  },
  [EndpointsView.NO_VULNERABILITY_MANAGER]: {
    title: `Endpoints with ${getPartnerEventName(PartnerEvents.NO_VULN_MANAGER)}`,
    query: { missing_vuln_manager: true },
    partnerEvents: [PartnerEvents.NO_VULN_MANAGER],
    controlCategory: ControlCategories.VULN_MANAGER,
  },
  [EndpointsView.NO_VULNERABILITY_SCAN]: {
    title: `Endpoints with ${getPartnerEventName(PartnerEvents.MISSING_SCAN)}`,
    query: { missing_vuln_scan: true },
    partnerEvents: [PartnerEvents.MISSING_SCAN],
    controlCategory: ControlCategories.VULN_MANAGER,
  },
  [EndpointsView.REDUCED_FUNCTIONALITY_MODE]: {
    title: (control) =>
      `Endpoints in ${getPartnerEventName(PartnerEvents.REDUCED_FUNCTIONALITY_MODE, control)}`,
    description: (control) =>
      control && <ReducedFunctionalityMode control={control} />,
    query: { instances: { any: { reduced_functionality_mode: true } } },
    partnerEvents: [PartnerEvents.REDUCED_FUNCTIONALITY_MODE],
  },
  [EndpointsView.EDR_CONTROL_FAILURES]: {
    title: `Endpoints with Control Failures`,
    query: {
      instances: {
        any: {
          or: [
            { no_av_policy: true },
            { no_edr_policy: true },
            { reduced_functionality_mode: true },
          ],
        },
      },
    },
    partnerEvents: [
      PartnerEvents.MISSING_AV_POLICY,
      PartnerEvents.MISSING_EDR_POLICY,
      PartnerEvents.REDUCED_FUNCTIONALITY_MODE,
    ],
  },
  [EndpointsView.VULNERABILITY_MANAGER_CONTROL_FAILURES]: {
    title: `Endpoints with Control Failures`,
    query: {
      instances: {
        any: {
          or: [
            { missing_scan: true },
            { out_of_date_scan: true },
            { has_agent: false },
          ],
        },
      },
    },
    partnerEvents: [
      PartnerEvents.MISSING_SCAN,
      PartnerEvents.OUT_OF_DATE_SCAN,
      PartnerEvents.USER_MISSING_VULN_MANAGER,
    ],
  },
  [EndpointsView.HOST_FIREWALL_CONTROL_FAILURES]: {
    title: `Endpoints with Control Failures`,
    query: {
      instances: {
        any: { missing_host_firewall: true },
      },
    },
    partnerEvents: [PartnerEvents.MISSING_HOST_FIREWALL_POLICY],
  },
  [EndpointsView.ENDPOINT_MANAGER_CONTROL_FAILURES]: {
    title: `Endpoints with Control Failures`,
    query: {
      instances: {
        any: { missing_asset_manager: true },
      },
    },
    partnerEvents: [PartnerEvents.NO_ASSET_MANAGER],
  },
  [EndpointsView.MISSING_AV_POLICY]: {
    title: `Endpoints with ${getPartnerEventName(PartnerEvents.MISSING_AV_POLICY)}`,
    query: { instances: { any: { no_av_policy: true } } },
    partnerEvents: [PartnerEvents.MISSING_AV_POLICY],
  },
  [EndpointsView.MISSING_EDR_POLICY]: {
    title: `Endpoints with ${getPartnerEventName(PartnerEvents.MISSING_EDR_POLICY)}`,
    query: { instances: { any: { no_edr_policy: true } } },
    partnerEvents: [PartnerEvents.MISSING_EDR_POLICY],
  },
  [EndpointsView.MISSING_HOST_FIREWALL]: {
    title: `Endpoints with ${getPartnerEventName(PartnerEvents.MISSING_HOST_FIREWALL_POLICY)}`,
    query: { instances: { any: { no_host_firewall_policy: true } } },
    partnerEvents: [PartnerEvents.MISSING_HOST_FIREWALL_POLICY],
  },
  [EndpointsView.MISSING_VULNERABILITY_AGENT]: {
    title: `Endpoints with ${getPartnerEventName(PartnerEvents.NO_VULN_MANAGER)}`,
    query: { instances: { any: { has_agent: false } } },
    partnerEvents: [PartnerEvents.NO_VULN_MANAGER],
  },
  [EndpointsView.MISSING_VULNERABILITY_SCAN]: {
    title: `Endpoints with ${getPartnerEventName(PartnerEvents.MISSING_SCAN)}`,
    query: { instances: { any: { missing_scan: true } } },
    partnerEvents: [PartnerEvents.MISSING_SCAN],
  },
  [EndpointsView.OUT_OF_DATE_VULNERABILITY_SCAN]: {
    title: `Endpoints with ${getPartnerEventName(PartnerEvents.OUT_OF_DATE_SCAN)}`,
    query: { instances: { any: { out_of_date_scan: true } } },
    partnerEvents: [PartnerEvents.OUT_OF_DATE_SCAN],
  },
};

const ReducedFunctionalityMode = ({
  control,
}: {
  control: ValidControlCode;
}) => {
  const controlName = getControlName(control);

  if (control === ControlCodes.CROWDSTRIKE) {
    return (
      <div className="flex flex-col items-start gap-4">
        <div>
          Reduced Functionality Mode (RFM) is a safe mode for the sensor that
          indicates a compatibility or configuration issue. The sensor is still
          connecting to the cloud, but is not fully functional.
        </div>
        <div className="flex flex-col">
          <Link href="https://falcon.us-2.crowdstrike.com/documentation/page/ecc97e75/falcon-sensor-for-windows-deployment#t66e9575">
            Windows Reduced Functionality Mode
          </Link>
          <Link href="https://falcon.us-2.crowdstrike.com/documentation/page/ea6bf997/falcon-sensor-for-linux-modes#r1167a77">
            Linux Reduced Functionality Mode
          </Link>
          <Link href="https://falcon.us-2.crowdstrike.com/documentation/page/e261a9b7/falcon-sensor-for-mac-deployment#pb0ee694">
            macOS Reduced Functionality Mode
          </Link>
        </div>
        <PolicyHostsReducedFunctionalityDeepLink control={control}>
          <Button
            intent="pill"
            size="tiny"
            icon={<ExternalLinkIcon className="text-secondary-foreground" />}
          >
            View endpoints in {getControlName(control)}
          </Button>
        </PolicyHostsReducedFunctionalityDeepLink>
      </div>
    );
  }

  if (control === ControlCodes.DEFENDER) {
    return (
      <div className="flex flex-col items-start gap-4">
        <div>
          When {controlName} is running in passive mode, it will not take
          preventative action against discovered threats. Files will be scanned
          and threats will be reported, but {controlName} is not treated as the
          primary antivirus on the endpoint.
        </div>
        <div className="flex flex-col">
          <Link href="https://learn.microsoft.com/en-us/defender-endpoint/microsoft-defender-antivirus-compatibility">
            {controlName} Antivirus compatibility with other security products
          </Link>
          <Link href="https://learn.microsoft.com/en-us/defender-endpoint/microsoft-defender-antivirus-windows#comparing-active-mode-passive-mode-and-disabled-mode">
            Comparing active mode, passive mode, and disabled mode
          </Link>
        </div>
      </div>
    );
  }

  if (control === ControlCodes.SENTINELONE) {
    return (
      <div className="flex flex-col items-start gap-4">
        <div>
          Each {controlName} agent has a status reported in the Management
          Console that tracks non-optimal conditions.
        </div>
        <div className="flex flex-col">
          <Link href="https://usea1-partners.sentinelone.net/docs/en/agent-operational-state-overview.html">
            Agent Operational State
          </Link>
        </div>
      </div>
    );
  }

  return null;
};

const Link = ({ children, href }: { children: ReactNode; href: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="flex items-center gap-1"
  >
    <ExternalLinkIcon />
    <span className="underline underline-offset-2">{children}</span>
  </a>
);
